import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { OUTRA_UNIDADE, unidadesComAtual } from "./unidades";

const inputCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";
const labelCls = "mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground";

export type DadosDeCompra = {
  subcategoryId: string;
  minimo: string;
  supplierPartnerId: string;
  unit: string;
  preco: string;
  fator: string;
  indirectConsumption: boolean;
};

export const COMPRA_VAZIA: DadosDeCompra = {
  subcategoryId: "",
  minimo: "",
  supplierPartnerId: "",
  unit: "",
  preco: "",
  fator: "",
  indirectConsumption: false,
};

/**
 * Onde o material vira custo: fornecedor, unidade, preço de compra e perda.
 *
 * Vem depois do nome do material no formulário. A categoria fica em `MaterialCategoriaLab`, que
 * abre o diálogo — ela identifica o material, não o custo dele.
 *
 * Tudo opcional de propósito — o laboratório cadastra o material quando conhece o material, e
 * preenche o preço quando conhece o preço. Exigir tudo de uma vez é o que faz um cadastro desses
 * nunca ser preenchido.
 */
export function MaterialCamposDeCompra({
  valor,
  onChange,
}: {
  valor: DadosDeCompra;
  onChange: (v: DadosDeCompra) => void;
}) {
  const [digitandoUnidade, setDigitandoUnidade] = useState(false);

  // Fornecedor é o Partner tipo SUPPLIER que já existe em Cadastros — sem cadastro paralelo.
  const fornecedoresQuery = trpc.partner.list.useQuery({ type: "SUPPLIER", take: 200, skip: 0 });

  const set = (p: Partial<DadosDeCompra>) => onChange({ ...valor, ...p });

  return (
    <>
      <div>
        <p className={labelCls}>Fornecedor</p>
        <select
          value={valor.supplierPartnerId}
          onChange={(e) => set({ supplierPartnerId: e.target.value })}
          className={inputCls}
        >
          <option value="">Sem fornecedor</option>
          {(fornecedoresQuery.data?.partners ?? []).map((p: { id: string; name: string | null }) => (
            <option key={p.id} value={p.id}>
              {p.name ?? "(sem nome)"}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[11px] text-zinc-500 dark:text-muted-foreground">
          Quem vende — diferente do fabricante. Cadastra-se em Cadastros, como fornecedor.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div>
          <p className={labelCls}>Unidade</p>
          {digitandoUnidade ? (
            <input
              value={valor.unit}
              onChange={(e) => set({ unit: e.target.value })}
              onBlur={() => !valor.unit.trim() && setDigitandoUnidade(false)}
              placeholder="Ex: folha"
              autoFocus
              className={inputCls}
            />
          ) : (
            <select
              value={valor.unit}
              onChange={(e) =>
                e.target.value === OUTRA_UNIDADE
                  ? (setDigitandoUnidade(true), set({ unit: "" }))
                  : set({ unit: e.target.value })
              }
              className={inputCls}
            >
              <option value="">—</option>
              {unidadesComAtual(valor.unit).map((u) => (
                <option key={u.valor} value={u.valor}>
                  {u.rotulo}
                </option>
              ))}
              <option value={OUTRA_UNIDADE}>+ Outra…</option>
            </select>
          )}
        </div>
        <div>
          <p className={labelCls}>Preço de compra</p>
          <input
            value={valor.preco}
            onChange={(e) => set({ preco: e.target.value })}
            placeholder="0,00"
            inputMode="decimal"
            className={inputCls}
          />
        </div>
        <div>
          <p className={labelCls}>Estoque mínimo</p>
          <input
            value={valor.minimo}
            onChange={(e) => set({ minimo: e.target.value })}
            placeholder="—"
            inputMode="decimal"
            className={inputCls}
          />
        </div>
        <div>
          <p className={labelCls}>Fator de perda</p>
          <input
            value={valor.fator}
            onChange={(e) => set({ fator: e.target.value })}
            placeholder="1,1"
            inputMode="decimal"
            className={inputCls}
          />
        </div>
      </div>
      <p className={cn("-mt-1 text-[11px] text-zinc-500 dark:text-muted-foreground")}>
        Fator 1,1 significa comprar 10% a mais do que a peça consome. O custo usa a quantidade
        comprada, não a consumida.
      </p>

      <label className="flex items-start gap-2 rounded-md border border-zinc-200 p-2.5 dark:border-border">
        <input
          type="checkbox"
          checked={valor.indirectConsumption}
          onChange={(e) => set({ indirectConsumption: e.target.checked })}
          className="mt-0.5"
        />
        <span className="text-[12.5px] leading-snug text-zinc-700 dark:text-foreground/80">
          Consumo indireto
          <span className="block text-[11px] text-zinc-500 dark:text-muted-foreground">
            Luva, álcool, broca compartilhada: controla estoque e compra, mas fica fora do custo de
            cada peça.
          </span>
        </span>
      </label>
    </>
  );
}
